import { useState } from 'react'
import { Percent } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { resourceProfilesApi, type ResourceProfile, type ResourceWorkType } from '@/api/resourceCenterApi'
import { WORK_TYPE_CHIPS } from '@/lib/assignmentGroups'
import { splitPayload, splitProblem, splitTotal, todayIso, type SplitDraft } from '@/lib/resourceCalendar'
import { DateInput, ErrorLine, Field, Segmented, WORK_COLOR, useProfileWrite } from './shared'

const PARTS: { key: 'processPct' | 'reviewPct' | 'qaPct' | 'supportPct'; type: ResourceWorkType }[] = [
  { key: 'processPct', type: 'PROCESS' },
  { key: 'reviewPct', type: 'REVIEW' },
  { key: 'qaPct', type: 'QA' },
  { key: 'supportPct', type: 'SUPPORT' },
]

const ALL_TYPES: ResourceWorkType[] = ['PROCESS', 'REVIEW', 'QA', 'SUPPORT']

/**
 * "+ New split" (slides 2 and 4): PR · RV · QA · SA adding up to at most 100,
 * or Dynamic — split as needed, limited to the work types chosen. Applies
 * from a date on; earlier days keep theirs.
 */
export function SplitDialog({
  profile,
  open,
  onOpenChange,
}: {
  profile: ResourceProfile
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const current = profile.split
  const [draft, setDraft] = useState<SplitDraft>(() => ({
    isDynamic: current?.isDynamic ?? false,
    processPct: current?.processPct ?? 0,
    reviewPct: current?.reviewPct ?? 0,
    qaPct: current?.qaPct ?? 0,
    supportPct: current?.supportPct ?? 0,
    workTypes: current?.isDynamic ? current.workTypes : ALL_TYPES,
  }))
  const [from, setFrom] = useState(todayIso())
  const write = useProfileWrite()
  const problem = splitProblem(draft)
  const total = splitTotal(draft)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2.5'>
            <span className='flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary'>
              <Percent className='h-4 w-4' />
            </span>
            New split for {profile.name}
          </DialogTitle>
          <DialogDescription>How their net hours split between the work, from a date on.</DialogDescription>
        </DialogHeader>

        <div className='space-y-3'>
          <Segmented
            ariaLabel='Split kind'
            value={draft.isDynamic ? 'dynamic' : 'fixed'}
            onChange={v => setDraft({ ...draft, isDynamic: v === 'dynamic' })}
            options={[
              { value: 'fixed', label: 'Percentages' },
              { value: 'dynamic', label: 'Dynamic' },
            ]}
          />
          <p className='text-xs text-muted-foreground'>
            <span className='font-medium text-foreground'>Percentages</span> — a fixed share of each day for Process,
            Review, QA and Support. <span className='font-medium text-foreground'>Dynamic</span> — split as needed by
            the scheduler, across the work types allowed.
          </p>

          {draft.isDynamic ? (
            <div className='space-y-2'>
              <div className='flex flex-wrap gap-1.5'>
                {ALL_TYPES.map(t => {
                  const on = draft.workTypes.includes(t)
                  return (
                    <button
                      key={t}
                      type='button'
                      aria-pressed={on}
                      onClick={() =>
                        setDraft({
                          ...draft,
                          workTypes: on ? draft.workTypes.filter(x => x !== t) : [...draft.workTypes, t],
                        })
                      }
                      className={cn(
                        'rounded-full border px-3 py-1 text-xs font-semibold transition-colors',
                        on ? WORK_TYPE_CHIPS[t].className + ' border-transparent' : 'text-muted-foreground',
                      )}
                    >
                      {WORK_TYPE_CHIPS[t].label} · {WORK_TYPE_CHIPS[t].title}
                    </button>
                  )
                })}
              </div>
            </div>
          ) : (
            <div className='space-y-2'>
              <div className='flex h-2 overflow-hidden rounded-full bg-muted'>
                {PARTS.map(p => (
                  <span
                    key={p.key}
                    className={cn(WORK_COLOR[p.type], 'transition-all')}
                    style={{ width: `${Math.min(draft[p.key], 100)}%` }}
                  />
                ))}
              </div>
              <div className='grid grid-cols-4 gap-2'>
                {PARTS.map(p => (
                  <Field key={p.key} label={`${WORK_TYPE_CHIPS[p.type].label} %`}>
                    <Input
                      type='number'
                      min={0}
                      max={100}
                      step={5}
                      inputMode='numeric'
                      value={draft[p.key]}
                      onChange={e => setDraft({ ...draft, [p.key]: Number(e.target.value) || 0 })}
                      className='h-9 text-sm tabular-nums'
                    />
                  </Field>
                ))}
              </div>
              <p className={cn('text-xs', total > 100 ? 'text-destructive' : 'text-muted-foreground')}>
                Total {total}% {total < 100 && total > 0 && '· the rest follows the same proportions'}
              </p>
            </div>
          )}

          <Field label='Applies from'>
            <DateInput value={from} onChange={setFrom} ariaLabel='Split applies from' />
          </Field>
          {problem && <p className='text-xs text-destructive'>{problem}</p>}
          <ErrorLine message={write.error} />
        </div>

        <DialogFooter>
          <Button variant='ghost' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!!problem || !from || write.pending}
            title={problem ?? undefined}
            onClick={() =>
              write.run(() => resourceProfilesApi.setSplit(profile.userId, splitPayload(draft, from)), () => onOpenChange(false))
            }
          >
            Save split
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
